import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FlyingRobots from '../components/FlyingRobots';
import {
  Plus, RefreshCw, Power, AlertCircle, CheckCircle2, Clock,
  Settings, Trash2, ExternalLink, Lock,
} from 'lucide-react';

const integrations = [
  {
    id: 'moka',
    name: 'Moka POS',
    category: 'POS',
    icon: '🏪',
    status: 'connected',
    lastSync: '5 minutes ago',
    syncing: ['Orders', 'Inventory', 'Pricing'],
    latency: 'Real-time',
  },
  {
    id: 'jurnal',
    name: 'Jurnal Accounting',
    category: 'Accounting',
    icon: '📊',
    status: 'connected',
    lastSync: '1 hour ago',
    syncing: ['Invoices', 'Expenses'],
    latency: '1-hour polling',
  },
  {
    id: 'majoo',
    name: 'Majoo POS',
    category: 'POS',
    icon: '🛒',
    status: 'disconnected',
    lastSync: null,
    syncing: [],
    latency: 'Real-time',
  },
  {
    id: 'sayurbox',
    name: 'Sayurbox',
    category: 'Inventory',
    icon: '🥬',
    status: 'connected',
    lastSync: '10 minutes ago',
    syncing: ['Inventory', 'Pricing'],
    latency: 'Real-time',
  },
  {
    id: 'xero',
    name: 'Xero Accounting',
    category: 'Accounting',
    icon: '📈',
    status: 'error',
    lastSync: '5 days ago',
    error: 'Auth token expired',
    syncing: [],
    latency: '2-hour polling',
  },
];

const IntegrationCard = ({ integration, onTest, onDisconnect, onManage, canManage }) => (
  <div className="border border-white/10 rounded-lg bg-white/[0.02] p-6 hover:border-white/20 transition-colors">
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <span className="text-[28px]">{integration.icon}</span>
        <div>
          <h3 className="h-sans text-[18px] font-medium">{integration.name}</h3>
          <p className="text-white/40 text-[12px]">{integration.category}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {integration.status === 'connected' && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-[11px] font-medium">
            <CheckCircle2 size={12} /> Connected
          </span>
        )}
        {integration.status === 'error' && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-[11px] font-medium">
            <AlertCircle size={12} /> Error
          </span>
        )}
        {integration.status === 'disconnected' && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/10 border border-white/20 text-white/60 text-[11px] font-medium">
            <Power size={12} /> Disconnected
          </span>
        )}
      </div>
    </div>

    {integration.status === 'error' && (
      <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/20">
        <p className="text-red-400 text-[12px]">{integration.error}</p>
      </div>
    )}

    {integration.status === 'connected' && (
      <div className="mb-4 space-y-2">
        <p className="text-white/60 text-[12px]">
          <Clock size={12} className="inline mr-1" /> {integration.lastSync}
        </p>
        <div className="flex flex-wrap gap-1">
          {integration.syncing.map((item) => (
            <span key={item} className="inline-block px-2 py-1 rounded text-[11px] bg-white/5 text-white/70">
              {item}
            </span>
          ))}
        </div>
        <p className="text-white/50 text-[11px]">Latency: {integration.latency}</p>
      </div>
    )}

    <div className="flex gap-2">
      {integration.status === 'connected' && canManage && (
        <>
          <button
            onClick={() => onTest(integration.id)}
            className="flex-1 py-2 px-3 rounded text-[12px] bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-colors"
          >
            <RefreshCw size={12} className="inline mr-1" /> Test
          </button>
          <button
            onClick={() => onDisconnect(integration.id)}
            className="flex-1 py-2 px-3 rounded text-[12px] bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
          >
            <Trash2 size={12} className="inline mr-1" /> Disconnect
          </button>
        </>
      )}
      {integration.status === 'disconnected' && canManage && (
        <button
          onClick={() => onManage(integration.id)}
          className="flex-1 py-2 px-3 rounded text-[12px] bg-[#10b981]/20 hover:bg-[#10b981]/30 text-[#10b981] transition-colors"
        >
          <Plus size={12} className="inline mr-1" /> Connect
        </button>
      )}
      {integration.status === 'error' && canManage && (
        <button
          onClick={() => onManage(integration.id)}
          className="flex-1 py-2 px-3 rounded text-[12px] bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 transition-colors"
        >
          <RefreshCw size={12} className="inline mr-1" /> Reconnect
        </button>
      )}
      {!canManage && (
        <button disabled className="flex-1 py-2 px-3 rounded text-[12px] bg-white/5 text-white/40 cursor-not-allowed">
          <Lock size={12} className="inline mr-1" /> Admin only
        </button>
      )}
    </div>
  </div>
);

export default function Integrations() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading || !user) {
    return null;
  }

  // Check if user is admin (has permission to manage integrations)
  // For now, only vintatan@imajiai.space can manage
  const canManage = user.email === 'vintatan@imajiai.space';
  const filteredIntegrations = activeTab === 'all'
    ? integrations
    : integrations.filter((i) => i.status === activeTab);

  const handleTest = (id) => {
    console.log('Testing sync for', id);
    // TODO: Call API to test sync
  };

  const handleDisconnect = (id) => {
    if (window.confirm(`Disconnect ${id}? Sync will stop.`)) {
      console.log('Disconnecting', id);
      // TODO: Call API to disconnect
    }
  };

  const handleManage = (id) => {
    console.log('Opening setup for', id);
    navigate(`/dashboard/integrations/${id}/setup`);
  };

  return (
    <div className="bg-[#050506] text-white relative min-h-screen">
      <FlyingRobots />
      <Navbar />

      <main className="pt-[100px] pb-20">
        <div className="mx-auto max-w-[1400px] px-6">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="eyebrow text-white/50 mb-2">Settings</p>
                <h1 className="h-display text-[48px] md:text-[56px] leading-[1.1]">Integrations</h1>
                <p className="text-white/60 text-[16px] mt-4 max-w-[600px]">
                  Connect your POS, accounting, and inventory systems to Grocera for real-time data sync.
                </p>
              </div>
              {canManage && (
                <button className="btn-primary inline-flex items-center gap-2">
                  <Plus size={16} /> Add Integration
                </button>
              )}
            </div>

            {!canManage && (
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 mb-6">
                <p className="text-blue-400 text-[13px]">
                  You have view-only access to integrations. Contact your account admin to manage connections.
                </p>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
              <p className="text-white/60 text-[13px] mb-1">Connected</p>
              <p className="h-sans text-[32px] font-medium text-emerald-400">
                {integrations.filter((i) => i.status === 'connected').length}
              </p>
            </div>
            <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
              <p className="text-white/60 text-[13px] mb-1">Available</p>
              <p className="h-sans text-[32px] font-medium">{integrations.length}</p>
            </div>
            <div className="p-4 rounded-lg border border-white/10 bg-white/[0.02]">
              <p className="text-white/60 text-[13px] mb-1">Errors</p>
              <p className="h-sans text-[32px] font-medium text-red-400">
                {integrations.filter((i) => i.status === 'error').length}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 border-b border-white/10">
            {['all', 'connected', 'error', 'disconnected'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-3 px-4 text-[13px] font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-[#10b981] text-white'
                    : 'border-transparent text-white/60 hover:text-white'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Integrations Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredIntegrations.map((integration) => (
              <IntegrationCard
                key={integration.id}
                integration={integration}
                onTest={handleTest}
                onDisconnect={handleDisconnect}
                onManage={handleManage}
                canManage={canManage}
              />
            ))}
          </div>

          {/* Roadmap */}
          <div className="mt-20 p-8 rounded-lg border border-white/10 bg-white/[0.02]">
            <h2 className="h-sans text-[24px] font-medium mb-4">Coming Soon</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-white/60 text-[13px] font-medium mb-2">Phase 2 (Q4 2026)</p>
                <ul className="space-y-2 text-white/70 text-[13px]">
                  <li>✓ Majoo POS real-time sync</li>
                  <li>✓ Accurate accounting integration</li>
                  <li>✓ Sync logs & audit trail</li>
                </ul>
              </div>
              <div>
                <p className="text-white/60 text-[13px] font-medium mb-2">Phase 3 (2027)</p>
                <ul className="space-y-2 text-white/70 text-[13px]">
                  <li>✓ Sayurbox inventory sync</li>
                  <li>✓ iSeller marketplace integration</li>
                  <li>✓ Custom webhooks & API</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
